import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { Field, Formik, Form, ErrorMessage } from "formik";
import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import { signIn } from "../../api/authService";
import { Icons } from "../../Components/Icons/Icons";
import SignImage from "../../Assets/Images/sign-in.webp"
import { useLoader } from '../../hooks/LoaderContext';

interface SignInData {
  email: string;
  password: string;
}

const SignInSchema = Yup.object().shape({
  email: Yup.string()
    .email(`Please ensure your email address includes the '@' symbol.`)
    .matches(
      /^(?!\.)(?!.*\.[.\s@])(?!.*\.$)[A-Z0-9._%+-]+@(?!\.)(?!.*\.[.\s])(?:[A-Z0-9-]+\.)+[A-Z]{2,}$/i,
      `Please ensure your email address includes the '@' symbol.`
    )
    .required(`Please ensure your email address includes the '@' symbol.`),
  password: Yup.string()
    .min(14, "Password must be at least 14 characters")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(
      /[\^$*.\[\]{}()?\-"'!@#%&/,><':;|_~`]/,
      "Password must contain at least one special character"
    )
    .required("Please fill the field"),
});

export const SignIn = () => {
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { setIsLoading } = useLoader();

  const handleSubmit = async (values: SignInData) => {
    setIsLoading(true);
    try {
      const session = await signIn(values.email, values.password);
      if (session && typeof session === "object" && 'AccessToken' in session) {
        const accessToken = session.AccessToken;
        if (accessToken) {
          sessionStorage.setItem("accessToken", accessToken);
          window.location.href = '/';
        } else {
          console.error("Session token was not set properly.");
        }
      } else if (session === "NEW_PASSWORD_REQUIRED") {
        navigate('/new-password');
      } else {
        console.error("SignIn session or AccessToken is undefined.");
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(`${error.toString().split(":")[1]}`);
      } else {
        setError("An unknown error occurred.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="sign">
      <img src={SignImage} alt="" />
      <div className="wrap">
        <div className="logo">
          {Icons.LogoIcon}
        </div>
        <div className="title">Log in to your account</div>
        <p>
          Please enter your details.
        </p>
        {error && (
          <Alert className="error" variant="outlined" severity="error">
            {error}
          </Alert>
        )}
        <Formik
          initialValues={{ email: "", password: "", remember: false }}
          validationSchema={SignInSchema}
          onSubmit={(values) => handleSubmit(values)}
        >
          {({ isSubmitting, touched, errors }) => (
            <Form>
              <label htmlFor="email">
                <span>Email</span>
                <Field
                  type="email"
                  name="email"
                  className={touched.email && errors.email ? "error" : ""}
                />
                <ErrorMessage
                  name="email"
                  component="div"
                  className="error-message"
                />
              </label>
              <label htmlFor="password">
                <span>
                  Password
                  <Link to="/reset-password">Forgot Password?</Link>
                </span>
                <Field
                  type="password"
                  name="password"
                  className={touched.password && errors.password ? "error" : ""}
                />
                <ErrorMessage
                  name="password"
                  component="div"
                  className="error-message"
                />
              </label>
              <button type="submit" disabled={isSubmitting} className="btn">
                Sign In
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </section>
  );
};
